import "./Contract.css";
import { useSelector } from "react-redux";
import { cartransferSelector } from "../../../features/cartransferSlice";
import { EyeOutlined } from "@ant-design/icons";
import n2words from "n2words";
import { format } from "rut.js";

const ACCENT = "#374151";
const LIGHT  = "#f9fafb";
const BORDER = "#e5e7eb";

const PanelHeader = ({ title }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      background: LIGHT,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      borderLeft: `4px solid ${ACCENT}`,
      marginBottom: 0,
    }}
  >
    <span style={{ color: ACCENT, fontSize: 16 }}><EyeOutlined /></span>
    <span style={{ fontWeight: 700, fontSize: 14, color: ACCENT, flex: 1 }}>{title}</span>
  </div>
);


const ContractStep = ({ label }) => {
  const cartransfer = useSelector(cartransferSelector);
  const { buyer, seller, buyers, sellers, contract } = cartransfer || {};
  // Usar arrays; fallback a singular envuelto en array para backward compat
  const sellerList = sellers?.length > 0 ? sellers : (seller ? [seller] : [{}]);
  const buyerList  = buyers?.length  > 0 ? buyers  : (buyer  ? [buyer]  : [{}]);
  const bg = cartransfer?.background || {};
  const perm = contract?.permission || cartransfer?.permission || {};
  const type = contract?.type || bg?.type || "";
  const make = contract?.make || bg?.make || "";
  const model = contract?.model || bg?.model || "";
  const year = contract?.year || bg?.year || "";
  const color = contract?.color || bg?.color || "";
  const motor = contract?.motor || bg?.motor || "";
  const chasis = contract?.chasis || bg?.chasis || "";
  const vin = contract?.vin || bg?.vin || "";
  const serial = contract?.serial || bg?.serial || "";
  const inscription = contract?.inscription || bg?.inscription || "";
  const gasRaw = contract?.gas || bg?.gas || perm?.gas || "";
  const otherGas = contract?.otherGas || "";
  const paymentMethod = contract?.paymentMethod || "EFECTIVO";
  const date = contract?.date || new Date().toISOString().split("T")[0];
  const payDate = contract?.payDate || date;
  const signDate = contract?.signDate || date;
  // Helper para convertir cadenas "2.100.000" o "$2.100.000" a número 2100000
  const toNumeric = (val) => {
    if (val === null || val === undefined) return NaN;
    const onlyDigits = String(val).replace(/\D+/g, "");
    if (!onlyDigits) return NaN;
    return Number(onlyDigits);
  };

  const appraisalRaw = contract?.appraisal || perm?.appraisal || bg?.appraisal || 1;
  const appraisalNum = (() => {
    const a = toNumeric(appraisalRaw);
    return !isNaN(a) && a > 0 ? a : 1;
  })();

  const priceNumber = (() => {
    const p = toNumeric(contract?.price);
    // Si el precio es mayor a 1, usarlo. Si es 0, NaN o 1, usar tasación.
    if (!isNaN(p) && p > 1) return p;
    return appraisalNum;
  })();
  const taxNumber = (() => {
    const t = toNumeric(contract?.tax);
    if (!isNaN(t) && t > 0) return t;
    return Math.round(priceNumber * 0.015);
  })();

  const isMillion = (num) => {
    try {
      if (!num || isNaN(num) || num <= 0) return "";
      const numValue = Number(num);
      return Number.isInteger(numValue / 1000000) ? " de" : "";
    } catch (error) {
      return "";
    }
  };
  const isUno = (string) => {
    if (!string || typeof string !== 'string') return "";
    try {
      return string.replace(/uno/g, "un");
    } catch (error) {
      return "";
    }
  };

  const dateOpts = { year: "numeric", month: "long", day: "numeric" };
  const toValidDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && value.toLowerCase().includes('invalid')) return null;
    // Admitimos ISO (con T) o "YYYY-MM-DD"
    const candidate = typeof value === 'string' && value.includes('T') ? value : `${value} 00:00`;
    const d = new Date(candidate);
    return isNaN(d.getTime()) ? null : d;
  };

  const txtDate = (value) => {
    const d = toValidDate(value);
    return d ? d.toLocaleDateString("es-ES", dateOpts) : "";
  };

  const txtFullDate = (value) => {
    const d = toValidDate(value);
    return d ? d.toLocaleDateString("es-ES", dateOpts) : "";
  };

  const fRut = (rut) => (!rut ? "" : format(rut));

  const getPersoneria = (person, role = "seller") => {
    const isBuyer = role === "buyer";
    if (person.representsCompany && person.companyName) {
      const repName = `${person.name || ""} ${person.firstLastName || ""} ${person.secondLastName || ""}`.trim();
      const statuteDate = person.companyStatuteDate ? txtFullDate(person.companyStatuteDate) : txtFullDate(new Date());
      return (
        <>
          La personería de <strong>{repName}</strong> para representar a{" "}
          <strong>{person.companyName}, RUT {fRut(person.companyRut)}</strong> en calidad de {isBuyer ? "compradora" : "vendedora"} consta en
          certificado de Estatuto actualizado del Registro de Empresas y Sociedades, otorgado por el Ministerio de
          Economía, Fomento y Turismo, de fecha {statuteDate}.
        </>
      );
    }
    if (!person.hasPower) return "";
    return (
      <>
        La personería de{" "}
        <span>{person.powerPersonName || ""} {person.powerPersonFirstLastName || ""} {person.powerPersonSecondLastName || ""}</span>{" "}
        para representar a{" "}
        <span>{person.name || ""} {person.firstLastName || ""} {person.secondLastName || ""}</span>{" "}
        consta en poder de {isBuyer ? "compra" : "venta"} de vehículo con fecha{" "}
        {person.powerDate && txtFullDate(person.powerDate)}, otorgado en la{" "}
        <span>{person.powerNotaryName}.</span>
      </>
    );
  };

  if (!cartransfer || !contract || !buyer || !seller) {
    return (
      <>
        <PanelHeader title={label || "Previsualización"} />
        <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", borderRadius: "0 0 10px 10px", padding: 20, background: "#fff", color: "#999", fontSize: 13 }}>
          Cargando datos del contrato...
        </div>
      </>
    );
  }

  if (!priceNumber && !taxNumber) {
    return (
      <>
        <PanelHeader title={label || "Previsualización"} />
        <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", borderRadius: "0 0 10px 10px", padding: 20, background: "#fff", color: "#999", fontSize: 13 }}>
          Esperando datos del contrato...
        </div>
      </>
    );
  }

  const PersonBlock = ({ person, index, total }) => {
    const isCompany = person.representsCompany && person.companyName;
    const repFullName = `${person.name || ""} ${person.firstLastName || ""} ${person.secondLastName || ""}`.trim();
    return (
      <div>
        {total > 1 && index > 0 && (
          <div style={{ borderTop: "1px solid #bbb", margin: "8px 0 6px", opacity: 0.5 }} />
        )}
        {isCompany ? (
          <>
            <div><strong>{person.companyName}</strong></div>
            <div><strong>RUT: {fRut(person.companyRut)}</strong></div>
            <div>DIRECCIÓN: {[person?.address?.toUpperCase(), person?.commune?.toUpperCase(), person?.region?.toUpperCase()].filter(Boolean).join(", ")}</div>
            <div>Fono: {person?.phone || "NO INFORMADO"}</div>
            <div>Representado por:</div>
            <div><strong>{repFullName}</strong></div>
            <div><strong>C.I: {fRut(person.rut)}</strong></div>
          </>
        ) : (
          <>
            <div><strong>{repFullName}</strong></div>
            <div>CÉDULA DE IDENTIDAD: {fRut(person?.rut || "")}</div>
            <div>DIRECCIÓN: {[person?.address?.toUpperCase(), person?.commune?.toUpperCase(), person?.region?.toUpperCase()].filter(Boolean).join(", ")}</div>
            <div>Fono: {person?.phone || "NO INFORMADO"}</div>
            {person.hasPower ? (
              <>
                <div>Representado por: <strong>{person.powerPersonName} {person.powerPersonFirstLastName} {person.powerPersonSecondLastName}</strong></div>
                <div><strong>C.I: {fRut(person.powerPersonRut)}</strong></div>
              </>
            ) : (
              <>
                <div>Email: {person?.email && !person.email.includes("@temp.digital-notary.cl") && !person.email.includes("@servel.digital-notary.cl") ? person.email : "NO INFORMADO"}</div>
                <div>Estado Civil: {person?.civilStatus?.toUpperCase() || "NO INFORMADO"}</div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const maxParties = Math.max(sellerList.length, buyerList.length);

  return (
    <>
      <PanelHeader title={label || "Previsualización"} />
      <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", borderRadius: "0 0 10px 10px", padding: "16px 20px", background: "#fff" }}>
      <div className="contract">
        <div className="contract__header">
          <h2>REPERTORIO N°{cartransfer?.repertory || ''}-{cartransfer?.repertoryYear || ''}</h2>
          <p>Puerto Montt, {txtDate(date)}</p>
        </div>

        <h2 className="contract__sale">COMPRAVENTA</h2>

        <div className="contract__persons">
          <span>Vendedor (a)</span>
          <span>Comprador (a)</span>
        </div>
        <table className="contract__personsTable">
          <tbody>
            <tr>
              <td>
                {sellerList.map((p, i) => (
                  <PersonBlock key={i} person={p} index={i} total={sellerList.length} />
                ))}
              </td>
              <td>
                {buyerList.map((p, i) => (
                  <PersonBlock key={i} person={p} index={i} total={buyerList.length} />
                ))}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="contract__summary">
          <p>Convienen en el siguiente contrato de compraventa de vehiculo:</p>
          <p>
            <span>PRIMERO:</span>{" "}
            {sellerList.map((p, i) => {
              const repName = `${p.name || ""} ${p.firstLastName || ""} ${p.secondLastName || ""}`.trim();
              if (p.representsCompany && p.companyName) {
                return <span key={i}>{i > 0 ? " y " : ""}<strong>{repName}</strong>, en representación de <strong>{p.companyName}, RUT {fRut(p.companyRut)}</strong></span>;
              }
              return <span key={i}>{i > 0 ? " y " : ""}<strong>{repName}</strong>{p.hasPower && <span> representad{p.sex === "F" ? "a" : "o"} por {p.powerPersonName} {p.powerPersonFirstLastName} {p.powerPersonSecondLastName}</span>}</span>;
            })}{" "}
            vende{sellerList.length > 1 ? "n" : ""}, cede{sellerList.length > 1 ? "n" : ""} y transfiere{sellerList.length > 1 ? "n" : ""} a{" "}
            {buyerList.map((p, i) => {
              const repName = `${p.name || ""} ${p.firstLastName || ""} ${p.secondLastName || ""}`.trim();
              if (p.representsCompany && p.companyName) {
                return <span key={i}>{i > 0 ? " y " : ""}<strong>{repName}</strong>, en representación de <strong>{p.companyName}, RUT {fRut(p.companyRut)}</strong></span>;
              }
              return <span key={i}>{i > 0 ? " y " : ""}<strong>{repName}</strong>{p.hasPower && <span> representad{p.sex === "F" ? "a" : "o"} por {p.powerPersonName} {p.powerPersonFirstLastName} {p.powerPersonSecondLastName}</span>}</span>;
            })}
            , quie{buyerList.length > 1 ? "nes compran y adquieren" : "n compra y adquiere"} para <span>SÍ</span>, el siguiente
            vehículo, combustible: {(gasRaw === "otro" ? otherGas : gasRaw) || ""}.
          </p>
        </div>

        <table className="contract__carTable">
          <tbody>
            <tr>
              <td>Tipo</td>
              <td>Marca</td>
              <td>Modelo</td>
              <td>Año</td>
            </tr>
            <tr>
              <td>{type || ""}</td>
              <td>{make || ""}</td>
              <td>{model || ""}</td>
              <td>{year || ""}</td>
            </tr>
            <tr>
              <td>Color</td>
              <td>N° motor</td>
              <td>N° CHASIS{vin && "/VIN"}{serial && "/SERIE"}</td>
              <td>Inscripción</td>
            </tr>
            <tr>
              <td>{color || ""}</td>
              <td>{motor || ""}</td>
              <td>
                <div>{chasis || ""}</div>
                {vin && <div>{vin}</div>}
                {serial && <div>{serial}</div>}
              </td>
              <td>{inscription || ""}</td>
            </tr>
          </tbody>
        </table>

        <div className="contract__conditions">
          <p>
            <span>SEGUNDO:</span> El vehículo se entrega, en el estado y condiciones que actualmente se encuentra, y que es conocida por la parte compradora, por lo que expresa no tener cargo alguno que formular.-
          </p>
          <p>
            <span>TERCERO:</span> El vendedor declara ser el único y exclusivo dueño del vehículo materia de este contrato y que se encuentra libre de toda prohibición, gravamen o litigio.-
          </p>
          <p>
            <span>CUARTO:</span> El precio de venta es la suma de{" "}
            <span>{`$${priceNumber.toLocaleString('es-CL')}`}</span>{" "}
            ({(() => {
              try {
                const numValue = parseInt(priceNumber);
                return priceNumber && !isNaN(numValue) && numValue > 0 ? isUno(n2words(numValue, { lang: "es" })) : "cero";
              } catch (e) { return "cero"; }
            })()} {isMillion(priceNumber)} pesos), que la parte compradora paga a la parte vendedora de la siguiente forma: <span>en {paymentMethod || "EFECTIVO"}.</span>
          </p>
          <p>
            <span>QUINTO:</span> Por tasa única del 1.5%, Impuesto Municipal, Ley 19.506, ascendente a la suma de{" "}
            <span>{`$${taxNumber.toLocaleString('es-CL')}`}</span>{" "}
            ({(() => {
              try {
                const numValue = parseInt(taxNumber);
                return taxNumber && !isNaN(numValue) && numValue > 0 ? isUno(n2words(numValue, { lang: "es" })) : "cero";
              } catch (e) { return "cero"; }
            })()} {isMillion(taxNumber)} pesos) se pagó el día de hoy,{" "}
            {txtDate(payDate) || "___"}, en línea, en el sitio web de la Tesorería General de La República. Doy fe.
          </p>
          <p>
            <span>SEXTO:</span> Se deja constancia que a la fecha de esta firma, el vendedor y el comprador no se encuentran inscritos como deudores en el Registro de Deudores de Pensiones de Alimentos. (Ley No 21.389). -
          </p>
          {[...sellerList, ...buyerList].some(p => p.hasPower || p.representsCompany) && (
            <p>
              <span>SÉPTIMO:</span>{" "}
              {sellerList.filter(p => p.hasPower || p.representsCompany).map((p, i) => <span key={`s${i}`}>{getPersoneria(p, "seller")} </span>)}
              {buyerList.filter(p => p.hasPower || p.representsCompany).map((p, i) => <span key={`b${i}`}>{getPersoneria(p, "buyer")} </span>)}
            </p>
          )}
        </div>

        <p style={{ fontSize: 12, marginBottom: 8 }}>
          Documento redactado bajo las expresas instrucciones de los comparecientes, quienes declaran haberlo leído íntegramente y ratificado la exactitud de la información proporcionada, sin tener reparo alguno que formular. Los comparecientes firman y estampan la impresión de su dígito pulgar derecho.
        </p>

        {Array.from({ length: maxParties }, (_, i) => {
          const s = sellerList[i];
          const b = buyerList[i];
          const sellerBox = {
            height: 100, width: 100,
            borderLeft: i < sellerList.length ? "1px solid black" : "none",
            borderRight: i < sellerList.length ? "1px solid black" : "none",
            borderTop: i === 0 && sellerList.length > 0 ? "1px solid black" : "none",
            borderBottom: i === sellerList.length - 1 ? "1px solid black" : "none",
          };
          const buyerBox = {
            height: 100, width: 100,
            borderLeft: i < buyerList.length ? "1px solid black" : "none",
            borderRight: i < buyerList.length ? "1px solid black" : "none",
            borderTop: i === 0 && buyerList.length > 0 ? "1px solid black" : "none",
            borderBottom: i === buyerList.length - 1 ? "1px solid black" : "none",
          };
          const sCi = s
            ? (s.representsCompany && s.companyRut
              ? `Vendedor${sellerList.length > 1 ? ` ${i + 1}` : ""} RUT: ${fRut(s.companyRut)}`
              : `Vendedor${sellerList.length > 1 ? ` ${i + 1}` : ""} CI: ${fRut(s?.rut)}`)
            : null;
          const bCi = b
            ? (b.representsCompany && b.companyRut
              ? `Comprador${buyerList.length > 1 ? ` ${i + 1}` : ""} RUT: ${fRut(b.companyRut)}`
              : `Comprador${buyerList.length > 1 ? ` ${i + 1}` : ""} CI: ${fRut(b?.rut)}`)
            : null;
          return (
            <div key={i}>
              <div className="contract__spaces">
                <div className="contract__line" style={{ visibility: s ? "visible" : "hidden" }}></div>
                <div style={sellerBox}></div>
                <div className="contract__line" style={{ visibility: b ? "visible" : "hidden" }}></div>
                <div style={buyerBox}></div>
              </div>
              <div className="contract__ids">
                <div>
                  {sCi && <div style={{ fontWeight: 700 }}>{sCi}</div>}
                  {s && (s.hasPower || s.representsCompany) && (
                    <div style={{ fontWeight: 700 }}>
                      <div>REPRESENTADO POR:</div>
                      <div>{s.representsCompany
                        ? `${s.name || ""} ${s.firstLastName || ""} ${s.secondLastName || ""}`.trim()
                        : `${s.powerPersonName || ""} ${s.powerPersonFirstLastName || ""} ${s.powerPersonSecondLastName || ""}`.trim()
                      }</div>
                      <div>C.I: {fRut(s.representsCompany ? s.rut : s.powerPersonRut)}</div>
                    </div>
                  )}
                </div>
                <div>
                  {bCi && <div style={{ fontWeight: 700 }}>{bCi}</div>}
                  {b && (b.hasPower || b.representsCompany) && (
                    <div style={{ fontWeight: 700 }}>
                      <div>REPRESENTADO POR:</div>
                      <div>{b.representsCompany
                        ? `${b.name || ""} ${b.firstLastName || ""} ${b.secondLastName || ""}`.trim()
                        : `${b.powerPersonName || ""} ${b.powerPersonFirstLastName || ""} ${b.powerPersonSecondLastName || ""}`.trim()
                      }</div>
                      <div>C.I: {fRut(b.representsCompany ? b.rut : b.powerPersonRut)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}


        <p style={{ marginTop: 8 }}>
          <strong>HABIENDO FIRMADO LAS PARTES Y PAGADOS LOS IMPUESTOS CORRESPONDIENTES, CON ESTA FECHA SE AUTORIZA Y LEGALIZA EL PRESENTE CONTRATO. EN PUERTO MONTT A {txtDate(signDate)?.toUpperCase() || "___"}, DOY FE.-</strong>
        </p>
      </div>
      </div>
    </>
  );
};

export default ContractStep;
